/**
 * 繰り返し支払いの各回を「支払い待ち」として作る(旧: 固定費の自動計上。2026-09-07 改修)。
 *
 * 支払う月を指定して、有効な繰り返し支払いから(2026-09-08: 支払日が基準。計上日は支払日に対する相対)
 *   - 取引(status='approved'=支払い待ち / payer='accounting' / source='recurring')… 承認済みと同じ扱いで
 *     PLの「見込み」に載る。経理が「支払い方法を入力する」と実績、承認者の完了確認でロック
 *   - 支払予定(cash_entries: planned)… 支払日でCFへ。支払い時に実際の金額・日付で「確定」に変わる
 * を生成する。
 *
 * 二重作成の防止: 取引のドキュメントIDを `rc_{設定ID}_{回のキー}` に固定する
 * (毎月=支払う月 / 毎週=本来の支払日)。同じ月を二度実行しても同じIDになるため、既存ぶんはスキップする。
 * 通常は経理・承認者がアプリを開いたときに当月〜翌々月に支払う分が自動で作られる(autoRecurring.ts)。
 */
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { fiscalYearOf, toMonth } from '@core/lib/date';
import { newCashEntryId } from '@core/cash/cashRepo';
import { nextTransactionCode } from '@core/lib/numbering';
import { writeAuditLog } from '@core/lib/auditLog';
import { markActionDone } from '@core/flow/notificationsRepo';
import { amountForMonth, isActiveInMonth } from './recurringRepo';
import { frequencyOf, occurrencesFor } from './schedule';
import type { CashAccount, MonthStr, RecurringCost, Transaction, Yen } from '@core/lib/types';

const TX = 'transactions';

export function recurringTransactionId(costId: string, key: string): string {
  return `rc_${costId}_${key}`;
}

export type PlanItem = {
  cost: RecurringCost;
  transactionId: string;
  amount: Yen;
  postingDate: string;
  paymentDate: string;
  /** 設定どおりの支払日(土日祝でずらす前) */
  scheduledDate: string;
  /** クレカのみ: カードの利用日 */
  usageDate: string | null;
  /** 既に作成済みならスキップする */
  alreadyPosted: boolean;
};

export type RecurringPlan = {
  month: MonthStr;
  items: PlanItem[];
  createCount: number;
  skipCount: number;
  totalAmount: Yen;
};

/** ID の in クエリは30件までのため分割して既存を調べる */
async function fetchExistingIds(ids: string[]): Promise<Set<string>> {
  const found = new Set<string>();
  for (let i = 0; i < ids.length; i += 30) {
    const chunk = ids.slice(i, i + 30);
    if (chunk.length === 0) continue;
    const snap = await getDocs(query(collection(getDb(), TX), where(documentId(), 'in', chunk)));
    snap.forEach((d: QueryDocumentSnapshot) => found.add(d.id));
  }
  return found;
}

/** 支払う月の作成計画を作る(書き込みは行わない) */
export async function buildRecurringPlan(params: {
  month: MonthStr;
  costs: RecurringCost[];
  /** 支払い方法マスタ(クレカの引き落とし日の計算に使う) */
  accounts?: CashAccount[];
}): Promise<RecurringPlan> {
  const { month, costs, accounts = [] } = params;
  const accountOf = (cost: RecurringCost) => accounts.find((a) => a.id === cost.cash_account_id) ?? null;
  const targets = costs.filter((c) => isActiveInMonth(c, month));
  const candidates = targets.flatMap((cost) =>
    occurrencesFor(cost, month, accountOf(cost)).map((o) => ({
      cost,
      transactionId: recurringTransactionId(cost.id, o.key),
      // 金額の改定は計上月(PLに載る月)で判定する
      amount: amountForMonth(cost, o.postingDate.slice(0, 7)),
      postingDate: o.postingDate,
      paymentDate: o.paymentDate,
      scheduledDate: o.scheduledDate,
      usageDate: o.usageDate ?? null,
    })),
  );
  const existing = await fetchExistingIds(candidates.map((c) => c.transactionId));
  const items: PlanItem[] = candidates.map((c) => ({ ...c, alreadyPosted: existing.has(c.transactionId) }));

  const pending = items.filter((i) => !i.alreadyPosted);
  return {
    month,
    items,
    createCount: pending.length,
    skipCount: items.length - pending.length,
    totalAmount: pending.reduce((sum, i) => sum + i.amount, 0),
  };
}

/** 計画を実行して「支払い待ち」を作る。作成済みのものは触らない */
export async function executeRecurringPlan(params: {
  plan: RecurringPlan;
  userId: string;
  fiscalStartMonth: number;
}): Promise<{ created: number; cashEntries: number }> {
  const { plan, userId, fiscalStartMonth } = params;
  const db = getDb();
  const targets = plan.items.filter((i) => !i.alreadyPosted);
  let created = 0;
  let cashEntries = 0;

  for (let offset = 0; offset < targets.length; offset += 100) {
    const batch = writeBatch(db);
    for (const item of targets.slice(offset, offset + 100)) {
      const { cost } = item;
      // 管理番号 F-YYYY-####(counters をトランザクションで採番)
      const code = await nextTransactionCode('F', Number(plan.month.slice(0, 4)));
      let cashEntryId: string | null = null;

      if (cost.cash_account_id) {
        cashEntryId = newCashEntryId();
        batch.set(doc(db, 'cash_entries', cashEntryId), {
          cash_account_id: cost.cash_account_id,
          date: item.paymentDate,
          direction: 'out',
          amount: item.amount,
          status: 'planned',
          description: cost.name,
          counterparty: cost.counterparty ?? null,
          transaction_id: item.transactionId,
          import_batch_id: null,
          dedup_hash: null,
          matched_at: null,
          matched_by: null,
          created_by: userId,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
        cashEntries++;
      }

      batch.set(doc(db, TX, item.transactionId), {
        type: 'expense',
        // 支払い待ち(承認済みと同じ段階)。経理が支払い、承認者が完了確認する
        status: 'approved',
        code,
        doc_type: null,
        payer: 'accounting',
        vendor_id: null,
        invoice_no: null,
        invoice_date: null,
        company_id: cost.company_id,
        department_id: cost.department_id,
        account_id: cost.account_id,
        sub_account_id: cost.sub_account_id,
        hierarchy_node_id: cost.hierarchy_node_id,

        amount: item.amount,
        applied_amount: item.amount,
        tax_mode: 'inclusive',
        tax_amount: null,

        posting_date: item.postingDate,
        posting_month: toMonth(item.postingDate),
        payment_date: item.paymentDate,
        payment_month: toMonth(item.paymentDate),
        fiscal_year: fiscalYearOf(item.postingDate, fiscalStartMonth),
        due_date: item.paymentDate,
        // クレカ(2026-09-08): カードの利用日。支払い入力で引き落とし日を計算する元になる
        purchase_date: item.usageDate,

        counterparty: cost.counterparty ?? null,
        description: cost.name,
        note: cost.description ?? null,
        payment_method: cost.payment_method,
        payment_account_id: cost.cash_account_id ?? null,
        evidence_type: null,

        attachments: [],
        receipts: [],

        is_internal: false,
        counterparty_department_id: null,
        // 立替ではない(セキュリティルールが advance を参照するため必ず持たせる)
        advance: false,

        approval_rule_id: '',
        approval_snapshot: [],
        current_step: 0,
        approvals: [],

        applied_at: null,
        applied_by: null,
        manager_checked_at: null,
        manager_checked_by: null,
        approved_at: serverTimestamp(),
        approved_by: null,
        paid_at: null,
        paid_by: null,
        completed_at: null,
        completed_by: null,
        submitted_at: null,
        submitted_by: null,
        confirmed_at: null,
        confirmed_by: null,
        rejected_at: null,
        rejected_by: null,
        reject_reason: null,
        rejected_from_status: null,

        source: 'recurring',
        cash_entry_id: cashEntryId,
        recurring_cost_id: cost.id,
        recurring_amount_varies: cost.amount_varies === true,

        created_by: userId,
        created_at: serverTimestamp(),
        updated_by: userId,
        updated_at: serverTimestamp(),
      });
      created++;
    }
    await batch.commit();
  }

  if (created > 0) {
    await writeBatch(db)
      .set(doc(collection(db, 'recurring_runs')), {
        month: plan.month,
        executed_at: serverTimestamp(),
        executed_by: userId,
        created_count: created,
        skipped_count: plan.skipCount,
      })
      .commit();
  }

  return { created, cashEntries };
}

/** 支払う月に作成済みの取引を取得する(取消の対象確認に使う) */
export async function fetchPostedTransactions(month: MonthStr): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), TX),
      where('source', '==', 'recurring'),
      where('payment_month', '==', month),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as Transaction);
}

/**
 * 支払う月の「支払い待ち」を取り消す(まだ支払っていない回の取引と支払予定を削除)。
 * 支払い済み・完了の回はお金が動いているため触らない。
 */
export async function undoRecurringPosting(params: {
  month: MonthStr;
  userId: string;
}): Promise<{ deleted: number; keptPaid: number }> {
  const db = getDb();
  const posted = await fetchPostedTransactions(params.month);
  let deleted = 0;
  let keptPaid = 0;

  for (let offset = 0; offset < posted.length; offset += 100) {
    const batch = writeBatch(db);
    for (const tx of posted.slice(offset, offset + 100)) {
      if (tx.status !== 'approved') {
        keptPaid++;
        continue;
      }
      if (tx.cash_entry_id) {
        const entries = await getDocs(
          query(
            collection(db, 'cash_entries'),
            where('transaction_id', '==', tx.id),
            where('status', '==', 'planned'),
          ),
        );
        entries.forEach((e) => batch.delete(e.ref));
      }
      batch.delete(doc(db, TX, tx.id));
      deleted++;
    }
    await batch.commit();
  }

  return { deleted, keptPaid };
}

/**
 * この回だけ支払わない(見送り・2026-09-07)。経理・承認者が支払い待ちの回を「取消」にし、
 * CFの支払予定(まだ予定のもの)も消す。履歴として取引は残る。
 */
export async function skipRecurringOccurrence(params: {
  transaction: Transaction;
  userId: string;
}): Promise<void> {
  const { transaction, userId } = params;
  if (transaction.source !== 'recurring' || transaction.status !== 'approved') {
    throw new Error('支払い待ちの繰り返し支払いだけ取り消せます');
  }
  const db = getDb();
  const batch = writeBatch(db);
  batch.update(doc(db, TX, transaction.id), {
    status: 'cancelled',
    cancelled_at: serverTimestamp(),
    cancelled_by: userId,
    updated_at: serverTimestamp(),
    updated_by: userId,
  });
  if (transaction.cash_entry_id) {
    const entry = await getDoc(doc(db, 'cash_entries', transaction.cash_entry_id));
    if (entry.exists() && (entry.data() as { status?: string }).status === 'planned') {
      batch.delete(entry.ref);
    }
  }
  await batch.commit();
  // 支払い依頼のお知らせ(バッジ)を済みにする
  await markActionDone(transaction.id);
  await writeAuditLog({
    targetType: 'transaction',
    targetId: transaction.id,
    action: 'recurring-skip',
    by: userId,
    before: { status: transaction.status },
    after: { status: 'cancelled' },
  });
}

/**
 * 設定を変えたとき、まだ支払っていない「支払い待ち」を新しい設定に合わせる(2026-09-08 指摘対応)。
 * 支払日・計上日・金額(改定を含む)・支払方法・口座・名称などを書き換え、期間外になった回や
 * 曜日が変わって該当しなくなった回は取消にする(CFの支払予定も合わせる)。
 * 支払い済み・完了・取消の回、および無効にした設定の回は触らない。
 * 新たに必要になった回は、呼び出し側が自動処理(force)で作る。
 */
export async function syncRecurringOccurrences(params: {
  cost: RecurringCost;
  userId: string;
  fiscalStartMonth: number;
  /** 支払い方法マスタ(クレカの引き落とし日の計算に使う) */
  accounts?: CashAccount[];
}): Promise<{ updated: number; cancelled: number }> {
  const { cost, userId, fiscalStartMonth, accounts = [] } = params;
  const account = accounts.find((a) => a.id === cost.cash_account_id) ?? null;
  if (!cost.active) return { updated: 0, cancelled: 0 };
  const db = getDb();
  const snap = await getDocs(
    query(collection(db, TX), where('recurring_cost_id', '==', cost.id), where('status', '==', 'approved')),
  );
  const prefix = `rc_${cost.id}_`;
  const weekly = frequencyOf(cost).kind === 'weekly';
  let updated = 0;
  let cancelled = 0;

  for (const d of snap.docs) {
    const tx = { id: d.id, ...(d.data() as object) } as Transaction;
    if (!tx.id.startsWith(prefix)) continue;
    const key = tx.id.slice(prefix.length); // 毎月=支払う月(YYYY-MM) / 毎週=本来の支払日(YYYY-MM-DD)
    const payMonth = key.slice(0, 7);
    const keyMatchesKind = weekly ? key.length === 10 : key.length === 7;
    const occ =
      keyMatchesKind && isActiveInMonth(cost, payMonth)
        ? occurrencesFor(cost, payMonth, account).find((o) => o.key === key)
        : undefined;

    if (!occ) {
      await skipRecurringOccurrence({ transaction: tx, userId });
      cancelled++;
      continue;
    }

    const amount = amountForMonth(cost, occ.postingDate.slice(0, 7));
    const next = {
      amount,
      applied_amount: amount,
      posting_date: occ.postingDate,
      posting_month: toMonth(occ.postingDate),
      payment_date: occ.paymentDate,
      payment_month: toMonth(occ.paymentDate),
      fiscal_year: fiscalYearOf(occ.postingDate, fiscalStartMonth),
      due_date: occ.paymentDate,
      purchase_date: occ.usageDate ?? null,
      company_id: cost.company_id,
      department_id: cost.department_id,
      account_id: cost.account_id,
      sub_account_id: cost.sub_account_id,
      hierarchy_node_id: cost.hierarchy_node_id,
      description: cost.name,
      note: cost.description ?? null,
      counterparty: cost.counterparty ?? null,
      payment_method: cost.payment_method,
      payment_account_id: cost.cash_account_id ?? null,
      recurring_amount_varies: cost.amount_varies === true,
    };
    const current = tx as unknown as Record<string, unknown>;
    const changed = (Object.keys(next) as Array<keyof typeof next>).some(
      (k) => (current[k] ?? null) !== (next[k] ?? null),
    );
    if (!changed) continue;

    const batch = writeBatch(db);
    let cashEntryId = tx.cash_entry_id ?? null;
    if (cashEntryId) {
      // CFの支払予定(まだ予定のもの)も新しい日付・金額・口座に合わせる
      const entry = await getDoc(doc(db, 'cash_entries', cashEntryId));
      if (entry.exists() && (entry.data() as { status?: string }).status === 'planned') {
        if (cost.cash_account_id) {
          batch.update(entry.ref, {
            cash_account_id: cost.cash_account_id,
            date: occ.paymentDate,
            amount,
            description: cost.name,
            counterparty: cost.counterparty ?? null,
            updated_at: serverTimestamp(),
          });
        } else {
          batch.delete(entry.ref);
          cashEntryId = null;
        }
      }
    } else if (cost.cash_account_id) {
      cashEntryId = newCashEntryId();
      batch.set(doc(db, 'cash_entries', cashEntryId), {
        cash_account_id: cost.cash_account_id,
        date: occ.paymentDate,
        direction: 'out',
        amount,
        status: 'planned',
        description: cost.name,
        counterparty: cost.counterparty ?? null,
        transaction_id: tx.id,
        import_batch_id: null,
        dedup_hash: null,
        matched_at: null,
        matched_by: null,
        created_by: userId,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });
    }
    batch.update(doc(db, TX, tx.id), {
      ...next,
      cash_entry_id: cashEntryId,
      updated_at: serverTimestamp(),
      updated_by: userId,
    });
    await batch.commit();
    // 支払日が変わったら、前の支払日で出したお知らせ(支払い依頼)は済みにする。期日なら自動処理が改めて知らせる
    if (tx.payment_date !== occ.paymentDate) await markActionDone(tx.id);
    updated++;
  }
  return { updated, cancelled };
}
